import { MenuTreeNode } from "src/api/client-api/menu/useNavigationMenu";
import {
    CanUserCallback,
    ORGANIZATION,
    PRODUCT,
    INGREDIENT,
    PACKAGING,
    SETTINGS,
    SUPPLIER,
    VIEW_OVERVIEW,
    VIEW_ORG_SETTINGS,
    VIEW,
    VIEW_NETWORK_TRACKING,
} from "src/context/FoodpilotContext";
import { canAccessSettings } from "src/pages/Settings/settingsAccessPermissions";

// Mapping of URL prefixes to required permissions
const URL_TO_PERMISSION_MAP: Record<string, { entity: string; action: Parameters<CanUserCallback>[0] }> = {
    "/products": { entity: PRODUCT, action: VIEW },
    "/product-lines": { entity: PRODUCT, action: VIEW },
    "/company": { entity: ORGANIZATION, action: VIEW_OVERVIEW },
    "/suppliers": { entity: SUPPLIER, action: VIEW },
    "/ingredients": { entity: INGREDIENT, action: VIEW },
    "/ingredient-lines": { entity: INGREDIENT, action: VIEW },
    "/packaging": { entity: PACKAGING, action: VIEW },
    "/packagings": { entity: PACKAGING, action: VIEW },
    "/distribution": { entity: PRODUCT, action: VIEW },
    "/transport": { entity: PRODUCT, action: VIEW },
    "/transformations": { entity: PRODUCT, action: VIEW },
    "/settings": { entity: SETTINGS, action: VIEW },
    "/company-settings": { entity: ORGANIZATION, action: VIEW_ORG_SETTINGS },
    "/company-settings/payment": { entity: ORGANIZATION, action: VIEW_ORG_SETTINGS },
    "/network": { entity: ORGANIZATION, action: VIEW_NETWORK_TRACKING },
};

const normalizeMenuUrl = (url: string): string => {
    // Menu items may store absolute URLs (e.g. "https://app.foodpilot.test/references_viticulteur")
    // while CMS pages store relative paths ("/references_viticulteur"). Extract the pathname so
    // both sides of the map lookup use the same shape.
    if (/^https?:\/\//i.test(url)) {
        try {
            return new URL(url).pathname;
        } catch {
            // fall through to relative handling
        }
    }
    return url.startsWith("/") ? url : `/${url}`;
};

/**
 * Extract the lifecycle route suffix from company-scoped menu URLs
 * (e.g. `/company/42/distribution` → `/distribution`).
 */
const lifecyclePathSuffix = (pathname: string): string | null => {
    const companyScopedMatch = pathname.match(/^\/company\/[^/]+(\/.+)$/);
    return companyScopedMatch?.[1] ?? null;
};

const resolveMenuPermissionKey = (menuUrl: string): string => {
    const pathname = normalizeMenuUrl(menuUrl);
    const suffix = lifecyclePathSuffix(pathname);

    if (suffix !== null && suffix in URL_TO_PERMISSION_MAP) {
        return suffix;
    }

    return pathname;
};

/**
 * Check if user has permission to access a menu based on its URL
 */
export const hasMenuPermission = (menuUrl: string | undefined, canUser: CanUserCallback): boolean => {
    if (!menuUrl) return true; // Drawers without URL are always visible

    const permissionKey = resolveMenuPermissionKey(menuUrl);

    if (permissionKey === "/settings") {
        return canAccessSettings(canUser);
    }

    const permission = URL_TO_PERMISSION_MAP[permissionKey];
    if (!permission) return true; // Unknown URL = visible by default

    return canUser(permission.action, permission.entity);
};

/**
 * Filter menu tree based on user permissions
 */
export const filterMenusByPermissions = (tree: MenuTreeNode[], canUser: CanUserCallback): MenuTreeNode[] => {
    return (
        tree
            .filter((node) => {
                return hasMenuPermission(node.item.url, canUser);
            })
            .map((node) => ({
                ...node,
                children: filterMenusByPermissions(node.children, canUser),
            }))
            // Remove empty drawers (items without URL that have no visible children)
            .filter((node) => {
                if (node.item.label === "separator") return true;
                if (node.item.url) return true; // Has URL = always keep
                return node.children.length > 0; // No URL = keep only if has visible children
            })
    );
};
